import { Injectable, NgZone } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import packageJson from '../../../../package.json';

// Keeps installed PWAs (handheld scanners in particular) on the latest build.
// The Angular service worker only applies a downloaded update on a full app
// restart, and scanners can keep the app open for days — so this service checks
// on a timer, surfaces a restart prompt the moment a new version is ready, and
// backs the manual Settings > Check for Updates button.
export type UpdateCheckResult = 'unavailable' | 'up-to-date' | 'updated' | 'error';

const PERIODIC_CHECK_MS = 6 * 60 * 60 * 1000; // 6 hours
const READY_TIMEOUT_MS = 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {

  public readonly version: string = packageJson.version;

  private updateReady = false;
  private updateReadyChanged = new Subject<boolean>();

  constructor(
    private swUpdate: SwUpdate,
    private ngZone: NgZone,
    private snackBar: MatSnackBar,
  ) {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => {
        this.setUpdateReady();
      });

    // The service worker's own cache is broken beyond repair (e.g. a partial
    // deploy removed files it still references) — a reload is the only fix.
    this.swUpdate.unrecoverable.subscribe(event => {
      console.error('Service worker unrecoverable state: ', event.reason);
      document.location.reload();
    });

    // Scanners rarely restart the app, so the on-launch check alone isn't
    // enough. Outside the Angular zone: the app's recurring polling means it
    // may never report "stable", and this timer must not add to that either.
    this.ngZone.runOutsideAngular(() => {
      setInterval(() => {
        this.swUpdate.checkForUpdate().catch(e => {
          console.log('Periodic update check failed: ', e);
        });
      }, PERIODIC_CHECK_MS);
    });
  }

  isSupported(): boolean {
    return this.swUpdate.isEnabled;
  }

  isUpdateReady(): boolean {
    return this.updateReady;
  }

  updateReadyObservable(): Observable<boolean> {
    return this.updateReadyChanged.asObservable();
  }

  // Settings > Check for Updates. Resolves once the outcome is known:
  // 'updated' means the new version is downloaded and ready — the caller
  // should restart (restartToUpdate) to apply it.
  checkForUpdates = async (): Promise<UpdateCheckResult> => {
    if (!this.swUpdate.isEnabled) {
      return 'unavailable';
    }

    try {
      if (this.updateReady) {
        return 'updated';
      }

      const updateFound = await this.swUpdate.checkForUpdate();

      if (!updateFound) {
        return 'up-to-date';
      }

      // checkForUpdate resolves as soon as a new version is detected; wait for
      // the service worker to finish downloading it before reporting success.
      await this.waitForUpdateReady();
      return 'updated';
    } catch (e) {
      console.error('Update check failed: ', e);
      return 'error';
    }
  }

  restartToUpdate() {
    if (this.swUpdate.isEnabled && this.updateReady) {
      this.swUpdate.activateUpdate().finally(() => {
        document.location.reload();
      });
    } else {
      document.location.reload();
    }
  }

  private setUpdateReady() {
    if (this.updateReady) {
      return;
    }
    this.updateReady = true;
    this.updateReadyChanged.next(true);

    const snackRef = this.snackBar.open('A new version of the app is ready.', 'Restart');
    snackRef.onAction().subscribe(() => {
      this.restartToUpdate();
    });
  }

  private waitForUpdateReady(): Promise<void> {
    if (this.updateReady) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        subscription.unsubscribe();
        reject(new Error('Timed out waiting for the update to download.'));
      }, READY_TIMEOUT_MS);

      const subscription = this.updateReadyChanged.subscribe(() => {
        clearTimeout(timeout);
        subscription.unsubscribe();
        resolve();
      });
    });
  }
}
